import type { ModelCard } from '@/api/modelCards.api'
import type { ExtraConfigFormState } from './agentFormTypes'

interface ModelLengths {
  name: string
  maxOutputLength: bigint
  contextLength: bigint
}

function validLength(value: bigint | number | null | undefined): bigint | undefined {
  if (typeof value === 'bigint') return value > 0n ? value : undefined
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0
    ? BigInt(value)
    : undefined
}

export function minimumLength(maas: bigint, card?: number | null): bigint {
  const left = validLength(maas)
  const right = validLength(card)
  if (left === undefined) return right ?? 0n
  return right === undefined || left < right ? left : right
}

export function applyModelCardLimits<T extends ModelLengths>(
  models: readonly T[],
  cards: readonly ModelCard[]
): T[] {
  const directory = new Map(cards.map(card => [card.name.trim(), card]))
  return models.map(model => {
    const card = directory.get(model.name.trim())
    return {
      ...model,
      maxOutputLength: minimumLength(model.maxOutputLength, card?.max_tokens),
      contextLength: minimumLength(model.contextLength, card?.max_context_length)
    }
  })
}

export function parameterForbidden(card: ModelCard | undefined, name: string): boolean {
  return card?.forbidden_params?.includes(name) ?? false
}

export function normalizeModelConfig(
  config: ExtraConfigFormState,
  card: ModelCard | undefined,
  model: ModelLengths
): ExtraConfigFormState {
  const enableThinking =
    typeof card?.support_thinking === 'boolean' ? card.support_thinking : config.enableThinking
  return {
    ...config,
    enableThinking,
    interleavedThinking:
      enableThinking === false || card?.support_interleaved_thinking === false
        ? false
        : config.interleavedThinking,
    maxOutputLength: model.maxOutputLength,
    contextLength: model.contextLength
  }
}

export function modelLengthError(config: ExtraConfigFormState): string | undefined {
  return config.maxOutputLength > 2147483647n || config.contextLength > 2147483647n
    ? '模型长度超出当前模板可保存范围，请选择其他模型'
    : undefined
}
